import { useEffect } from "react";
import { useNavigate, useParams, useSearchParams, Navigate } from "react-router-dom";
import Nav from "@/components/Nav";
import CategoryBar, { STORE_CATEGORIES } from "@/components/CategoryBar";
import Hero from "@/components/Hero";
import Marquee from "@/components/Marquee";
import Shop from "@/components/Shop";
import Newsletter from "@/components/Newsletter";
import Footer from "@/components/Footer";
import CartDrawer from "@/components/CartDrawer";
import Seo, { SITE_URL } from "@/components/Seo";
import { CartProvider } from "@/components/CartContext";

const catSlug = (c) => c.toLowerCase().replace(/[^a-z0-9]+/g, "-");
const SLUG_TO_CAT = Object.fromEntries([
  ...STORE_CATEGORIES.map((c) => [catSlug(c), c]),
  ["trending", "Trending"],
]);

const CATEGORY_SEO = {
  Trending: ["Trending Candy & Toys in Canada", "Shop the hottest trending candy, toys and sensory finds in Canada right now. Hand-picked hot sellers, shipped across Canada from Nova Scotia."],
  Candy: ["Buy Candy Online in Canada", "Shop Canadian candy online — retro favourites, nostalgic classics and trending treats. Shipped across Canada from Meteghan River, Nova Scotia."],
  Sensory: ["Sensory Toys & Squishies in Canada", "Shop squishies, fidgets and sensory toys online in Canada. Satisfying, giftable and shipped across Canada from Nova Scotia."],
  Toys: ["Buy Toys Online in Canada", "Shop fun toys, plushies and collectibles online in Canada. Hand-packed in Nova Scotia and shipped across Canada."],
  "Charms & Accessories": ["Charms & Accessories in Canada", "Shop cute charms and accessories online in Canada. Small treats, big personality — shipped across Canada from Nova Scotia."],
  "Mini Add-Ons": ["Mini Add-Ons Under $5 in Canada", "Shop mini add-ons and impulse treats under $5 in Canada. Perfect little extras for any candy or toy order."],
  Seasonal: ["Seasonal Candy & Toys in Canada", "Shop seasonal candy and toys for every holiday in Canada. Limited-time treats shipped across Canada from Nova Scotia."],
};

export default function Landing() {
  const navigate = useNavigate();
  const params = useParams();
  const [searchParams] = useSearchParams();

  const category =
    SLUG_TO_CAT[params.catSlug] ||
    (searchParams.get("cat") && (SLUG_TO_CAT[catSlug(searchParams.get("cat"))] || null)) ||
    null;

  const pickCategory = (cat) => {
    navigate(cat ? `/category/${catSlug(cat)}` : "/");
  };

  useEffect(() => {
    if (category) {
      setTimeout(() => document.getElementById("shop")?.scrollIntoView({ behavior: "smooth" }), 150);
    }
  }, [category]);

  if (params.catSlug && !SLUG_TO_CAT[params.catSlug]) {
    return <Navigate to="/" replace />;
  }

  const [seoTitle, seoDesc] = category
    ? CATEGORY_SEO[category] || [`${category} in Canada`, `Shop ${category} online in Canada at Canadian Beats Marketplace.`]
    : ["Canadian Beats Marketplace | Candy, Toys & Sensory Toys from Nova Scotia, Canada",
       "Shop trending Canadian candy, toys, squishies and sensory toys online. Hand-packed in Meteghan River, Nova Scotia and shipped across Canada. Local pickup available."];
  const canonical = category ? `${SITE_URL}/category/${catSlug(category)}` : `${SITE_URL}/`;
  const jsonLd = category
    ? {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
          { "@type": "ListItem", position: 2, name: category, item: canonical },
        ],
      }
    : null;

  return (
    <CartProvider>
      <main data-testid="landing-page" className="overflow-x-clip">
        <Seo title={`${seoTitle}${category ? " | Canadian Beats Marketplace" : ""}`} description={seoDesc} canonical={canonical} jsonLd={jsonLd} />
        <Nav onBrandClick={() => pickCategory(null)} />
        <CategoryBar active={category} />
        <Hero />
        <Marquee />
        <Shop category={category} onPickCategory={pickCategory} />
        <Newsletter />
        <Footer />
        <CartDrawer />
      </main>
    </CartProvider>
  );
}
